import * as Sentry from "@sentry/vue";
import { ref, computed, readonly } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { clearToken, setToken } from "../../apollo-client/apollo-token";
import { resetCache } from "../../apollo-client/vue-apollo";
import {
  setLastRoute,
  $lastRoute,
  clearLastRoute,
} from "../../router/auth-hooks";
import {
  AUTHENTICATE_USER,
  CHANGE_PASSWORD,
  UPDATE_PASSWORD,
  CREATE_HUBSPOT_CONTACT,
} from "./graphql";
import useFacility from "./useFacility";
import useAccessibleScope, {
  deriveAccessibleScope,
  clearAccessibleScope,
} from "./useAccessibleScope";
import useDashboard from "@components/dashboard/useDashboard";
import { setPopupSmartUserData } from "../../plugins/popupsmart";

const currentUser = ref(null);
const inProcess = ref(false);
const authenticated = computed(() => !!currentUser.value);

const defaultOrganization = computed(
  () => currentUser.value?.defaultOrganization
);

const isFacilityManager = computed(
  () =>
    defaultOrganization.value?.type === "facility" &&
    !isMultiFacilityManager.value
);

const isMultiFacilityManager = computed(() => {
  if (!currentUser.value) return false;
  const { reachesMultipleFacilities, singleFacilityScope } =
    useAccessibleScope();
  return reachesMultipleFacilities.value && singleFacilityScope.value === null;
});

const singleFacilityScope = computed(() => {
  const { singleFacilityScope } = useAccessibleScope();
  return singleFacilityScope.value;
});

export function scopeAppliesToDefaultOrg() {
  return ["region", "facility"].includes(defaultOrganization.value?.type);
}

// Map a login failure to user-facing copy. Prefers a server error code
// (extensions.code) but falls back to matching the message the backend actually
// sends (atom-derived strings like "not_authenticated"/"not_authorized"). Keeps
// credential failures generic so we never reveal whether a username exists.
export function loginErrorMessage(error) {
  const gqlError = error?.graphQLErrors?.[0];
  const signal = `${gqlError?.extensions?.code || ""} ${
    gqlError?.message || error?.message || ""
  }`.toLowerCase();
  const has = (needle) => signal.includes(needle);

  // `not_authorized` covers both a deactivated account and one whose roles grant
  // no access to this app: the backend returns the same code for each so that
  // the active flag never leaks whether a username exists. The copy therefore
  // names neither, and points at the one action that resolves both.
  if (has("not_authorized")) {
    return "Your account doesn't have access to this app. Contact your administrator if you think this is a mistake.";
  }
  if (has("rate") || has("too_many") || has("throttle")) {
    return "Too many attempts. Please wait a moment and try again.";
  }
  // Kept for an explicit code from the gateway or a future backend error: the
  // login path itself has no deactivation-specific code today.
  if (has("locked") || has("disabled") || has("suspended")) {
    return "This account is locked. Please contact your administrator.";
  }
  if (has("not_authenticated") || has("invalid") || has("credential")) {
    return "Incorrect username or password.";
  }
  // No GraphQL errors at all → a transport/network failure.
  if (!error?.graphQLErrors?.length) {
    return "Unable to connect. Please check your connection and try again.";
  }
  return "Login failed, please try again.";
}

const twoFactorRequired = computed(
  () => currentUser.value?.twoFactorRequired || false
);

const canAdministerUsers = computed(() => {
  const user = currentUser.value;
  if (!user || !Array.isArray(user.roles)) return false;
  return user.roles.some(
    (r) =>
      r?.canAdministerUsers === true && r?.role?.canAdministerUsers === true
  );
});

const canApproveBulkOperations = computed(() => {
  const user = currentUser.value;
  if (!user) {
    console.log("[useUser] canApproveBulkOperations: no user");
    return false;
  }

  const hasAdminRole = Array.isArray(user.roles)
    ? user.roles.some(
        (r) => r?.role?.name === "backoffice_admin" || r?.role?.name === "admin"
      )
    : false;

  const isFacilityMgr = isFacilityManager.value === true;
  const result = hasAdminRole || isFacilityMgr;
  return result;
});

const canApproveFacility = (facilityId) => {
  const user = currentUser.value;
  if (!user || !facilityId) return false;

  const hasAdminRole = Array.isArray(user.roles)
    ? user.roles.some(
        (r) => r?.role?.name === "backoffice_admin" || r?.role?.name === "admin"
      )
    : false;
  if (hasAdminRole) return true;

  if (isFacilityManager.value === true) {
    const assigned = Array.isArray(user.assignedFacilities)
      ? user.assignedFacilities.includes(facilityId)
      : false;
    const isDefault = user.defaultOrganization?.id === facilityId;
    return assigned || isDefault;
  }
  return false;
};

export const defaultPage = computed(() => {
  if (!authenticated.value) {
    return { name: "Login" };
  }

  if (currentUser.value?.username === "cpotash@citylinecapital.com") {
    return { name: "ManagementDashboardTrendReport" };
  }
  // Navigation scope is the union of the user's role subtrees, not the single
  // default org. A multi-scope user (any region, or >1 facility) lands on the
  // management summary with a facility switcher; a user whose whole scope is one
  // facility drops straight into that facility.
  const { reachesMultipleFacilities, singleFacilityScope, hasSystemScope } =
    useAccessibleScope();

  if (singleFacilityScope.value) {
    return {
      name: "FacilityDashboard",
      params: { organization_id: singleFacilityScope.value.id },
    };
  }

  // A platform/application admin lands on the org-wide management summary even
  // when their default org is a single facility: their scope is the whole tree,
  // and the header switcher takes them into any facility. Dropping them onto one
  // facility's dashboard would hide the rest of the system their role covers.
  if (hasSystemScope.value) {
    return { name: "ManagementSummary" };
  }

  // A facility-type default org lands on its own dashboard only when that
  // facility is the user's whole working context (a plain facility manager, or
  // scope not yet loaded). A multi-scope user, e.g. a cross-region manager whose
  // default org happens to be a facility, lands on the management summary below
  // instead: login seeds no facility context for them, so routing to the
  // facility strands them on "Page not found", and the summary with its facility
  // switcher is the right overview for their wider scope.
  const orgType = defaultOrganization.value?.type;
  if (orgType === "facility" && !reachesMultipleFacilities.value) {
    return {
      name: "FacilityDashboard",
      params: { organization_id: defaultOrganization.value.id },
    };
  }

  // Fallbacks for the legacy single-default shape (scope not yet loaded).
  if (
    orgType === "organization" ||
    orgType === "region" ||
    reachesMultipleFacilities.value
  ) {
    return { name: "ManagementSummary" };
  }

  return { name: "ManagementDashboard" };
});

// Which facility completeLogin should seed, in precedence order: the only
// facility in scope, then the facility a restored deep link points at, then a
// facility manager's own default org. Null when the landing route is org-wide
// and no facility context is needed.
function facilityToSeedAfterLogin() {
  if (singleFacilityScope.value) {
    return singleFacilityScope.value;
  }

  const lastOrgId = $lastRoute.value?.params?.organization_id;
  if (lastOrgId) {
    return { id: lastOrgId };
  }

  const { reachesMultipleFacilities } = useAccessibleScope();
  if (reachesMultipleFacilities.value && !canViewOrganizationTree.value) {
    return null;
  }

  return isFacilityManager.value && !isMultiFacilityManager.value
    ? defaultOrganization.value
    : null;
}

export const canViewOrganizationTree = computed(() => {
  const user = currentUser.value;
  if (!user) return false;
  const { hasSystemScope } = useAccessibleScope();
  return (
    (hasSystemScope.value ||
      ["platform", "application", "organization", "region"].includes(
        defaultOrganization.value?.type
      )) &&
    !singleFacilityScope.value
  );
});

function useUser() {
  const $router = useRouter();
  const $route = useRoute();

  const { selectFacility, clearCurrentFacility, currentFacility } =
    useFacility();

  const { clearDashboardData } = useDashboard();

  async function login(dollarApollo, username, password, recaptchaToken) {
    inProcess.value = true;
    let logged = false;
    let message = "";
    try {
      const response = await dollarApollo.mutate({
        mutation: AUTHENTICATE_USER,
        variables: {
          username,
          password,
          recaptchaToken,
        },
        fetchPolicy: "no-cache",
      });

      const loginResult = response.data?.login;
      if (!loginResult) {
        throw new Error("Malformed login response");
      }
      const { token, twoFactorRequired, twoFactorSetupRequired, user } =
        loginResult;
      if (twoFactorSetupRequired) {
        $router.push({
          name: "TwoFactorSetup",
          params: {
            token: token,
          },
        });
        return { logged, message };
      } else if (twoFactorRequired) {
        $router.push({
          name: "TwoFactorVerification",
          params: {
            token: token,
            twoFactorMethod: user.twoFactorMethod,
          },
        });
        return { logged, message };
      }
      // Persist the token explicitly at the call site. The auth-link also does
      // this as a side effect of the AuthenticateUser response, but making it
      // an intentional step here matches the 2FA paths and removes the hidden
      // coupling completeLogin used to rely on.
      setToken(token);
      user.twoFactorRequired = twoFactorRequired;
      logged = true;
      await completeLogin(dollarApollo, user);
    } catch (error) {
      currentUser.value = null;
      message = loginErrorMessage(error);
      Sentry.captureException(error);
    } finally {
      inProcess.value = false;
    }
    return { logged, message };
  }

  // Finishes a successful authentication (password login, or after 2FA
  // setup/verification): stores the user, loads the chatbot, establishes the
  // facility context, and navigates to the landing route. The caller is expected
  // to have persisted the auth token first (via setToken) — the password path
  // does so in `login`, the 2FA paths in their verify handlers. Shared so every
  // login path leaves the app fully initialized (facility context is required by
  // Dashboard/Smart Units).
  async function completeLogin(dollarApollo, user) {
    setCurrentUser(user);

    // Analytics widget — fire-and-forget. It must NEVER gate the login redirect:
    // awaiting this third-party (HubSpot) call meant a slow/hung response left
    // the user stranded on /login behind a skeleton. It has its own error
    // handling, so a rejection here is harmless.
    loadChatbot(dollarApollo, user);

    // The navigable scope is derived from the login user's roles inside
    // `setCurrentUser` above (each role carries its org + ancestors + descendants),
    // so it's already set here — no separate query. The facility seed below and
    // `defaultPage` read it synchronously.

    // Seed the facility, using the scope loaded above: the single
    // facility in scope, a restored facility route, or a facility manager's own
    // default org — FacilityDashboard is wrapped by FacilityWrapper, which 404s
    // without a currentFacilityId. A failure here must not strand the user on
    // /login or silently log them out, so capture it and fall through to
    // navigation.
    try {
      const facility = facilityToSeedAfterLogin();
      if (facility) {
        await selectFacility(dollarApollo, facility);
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    const target = $lastRoute.value || defaultPage.value;
    // Consume the saved deep link so a stale destination isn't reused on the
    // next login in this tab.
    clearLastRoute();
    $router.replace(target).catch((err) => {
      if (err.name !== "NavigationDuplicated") throw err;
    });
  }

  async function loadChatbot(dollarApollo, user) {
    try {
      const response = await dollarApollo.mutate({
        mutation: CREATE_HUBSPOT_CONTACT,
        fetchPolicy: "no-cache",
      });

      if (!response.data?.createHsContact?.token) {
        return;
      }

      loadChatWidget(user, response.data.createHsContact.token);
    } catch (error) {
      console.error(error);
    }
    return;
  }

  async function logout(dollarApollo) {
    await clearUser(dollarApollo);
    // Remove HubSpot chat widget on logout
    if (window.HubSpotConversations && window.HubSpotConversations.widget) {
      window.HubSpotConversations.widget.remove();
    }
  }

  async function requestPasswordReset(dollarApollo, username) {
    inProcess.value = true;
    let success = false;
    let message = "";

    try {
      const response = await dollarApollo.mutate({
        mutation: CHANGE_PASSWORD,
        variables: {
          username,
        },
        fetchPolicy: "no-cache",
      });
      success = response.data?.changePassword?.status === "ok";
      message = response.data?.changePassword?.message;
    } catch (error) {
      // console.error(error);
      message = error.message?.replace("GraphQL", "Forgot Password");
    }
    inProcess.value = false;
    return [success, message];
  }

  async function resetPassword(dollarApollo, newPassword) {
    const token = $route.query?.token;

    if (!token) {
      return false;
    }

    inProcess.value = true;
    let success = false;
    try {
      const response = await dollarApollo.mutate({
        mutation: UPDATE_PASSWORD,
        variables: {
          password: newPassword,
          token,
        },
      });
      success = response.data.updatePassword.status === "ok";
    } catch (error) {
      // console.error(error);
    }
    inProcess.value = false;
    return success;
  }

  async function clearUser(dollarApollo) {
    clearCurrentFacility();
    clearDashboardData();
    clearAccessibleScope();
    clearCurrentUser();
    clearToken();
    await resetCache(dollarApollo.provider.defaultClient);
  }

  function displayUserName(user) {
    return `${user?.firstName} ${user?.lastName || ""}`;
  }

  return {
    inProcess: readonly(inProcess),
    currentUser: readonly(currentUser),
    authenticated,
    defaultOrganization,
    isFacilityManager,
    isMultiFacilityManager,
    scopeAppliesToDefaultOrg,
    singleFacilityScope,
    canAdministerUsers,
    canApproveBulkOperations,
    canApproveFacility,
    login,
    completeLogin,
    logout,
    requestPasswordReset,
    resetPassword,
    defaultPage,
    canViewOrganizationTree,
    clearUser,
    restoreCurrentUser,
    displayUserName,
    loadChatbot,
    setCurrentUser,
    twoFactorRequired,
  };
}
export default useUser;

const STORE_USER = "currentUser";
export function setCurrentUser(user) {
  currentUser.value = user;

  try {
    window.localStorage.setItem(STORE_USER, JSON.stringify(user));
  } catch (error) {
    // A refused write must not abort sign-in. Everything below still runs and
    // the session reads the in-memory user; only surviving a reload is lost.
    Sentry.captureException(error);
  }

  // Derive the navigable scope from the user's roles (org + ancestors +
  // descendants carried on the login response), so it's ready without a query.
  deriveAccessibleScope(user);
  Sentry.setUser({
    id: user.id,
    username: user.username,
    segment: user?.defaultOrganization?.name,
  });

  if (typeof _kmq !== "undefined" && _kmq) {
    _kmq.push(["identify", user.username]);
    _kmq.push([
      "set",
      {
        "User Id": user.id,
        "User Name": user.username,
        "Region/Facility": user?.defaultOrganization?.name,
      },
    ]);
  }

  // Update PopupSmart with user data
  setTimeout(() => {
    setPopupSmartUserData(user);
  }, 1000);
}

function restoreCurrentUser() {
  const user = window.localStorage.getItem(STORE_USER);
  if (user) {
    currentUser.value = JSON.parse(user);
    // Re-derive scope from the stored roles on reload — no query needed.
    deriveAccessibleScope(currentUser.value);
    Sentry.setUser({
      id: currentUser.value.id,
      username: currentUser.value.username,
      segment: currentUser.value?.defaultOrganization?.name,
    });

    // Update PopupSmart with restored user data
    setTimeout(() => {
      setPopupSmartUserData(currentUser.value);
    }, 1000);

    return currentUser.value;
  }
  return null;
}

function clearCurrentUser() {
  window.localStorage.removeItem(STORE_USER);
  currentUser.value = null;
  Sentry.setUser(null);
}

const DEVICE_FINGERPRINT_COOKIE_NAME = "_sd_device_fingerprint";

export function getDeviceFingerprint() {
  const deviceFingerprint = getCookie(DEVICE_FINGERPRINT_COOKIE_NAME);
  if (deviceFingerprint) {
    return deviceFingerprint;
  }
  const fingerprint = generateDeviceFingerprint();
  setCookie(DEVICE_FINGERPRINT_COOKIE_NAME, fingerprint, {
    maxAge: 60 * 60 * 24 * 90,
    secure: true,
    sameSite: "Lax",
  });
  return fingerprint;
}

function generateDeviceFingerprint() {
  const navigatorInfo = window.navigator;
  const screenInfo = window.screen;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const data = [
    navigatorInfo.userAgent,
    navigatorInfo.language,
    screenInfo.colorDepth,
    screenInfo.width,
    screenInfo.height,
    screenInfo.availWidth,
    screenInfo.availHeight,
    timezone,
    navigatorInfo.hardwareConcurrency,
    navigatorInfo.deviceMemory,
  ].join("::");

  return hashString(data);
}

function hashString(str) {
  let hash = 0,
    i,
    chr;
  if (str.length === 0) return hash.toString();
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash.toString();
}

function setCookie(name, value, options = {}) {
  const {
    maxAge, // in seconds
    path = "/", // default path
    domain, // optional
    secure, // boolean
    sameSite = "Lax", // 'Lax', 'Strict', or 'None'
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (maxAge !== undefined) {
    cookieString += `; max-age=${maxAge}`;
  }

  if (path) {
    cookieString += `; path=${path}`;
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += `; Secure`;
  }

  if (sameSite) {
    cookieString += `; SameSite=${sameSite}`;
  }

  document.cookie = cookieString;
}

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}

function loadChatWidget(user, token, refresh = false) {
  if (window.HubSpotConversations) {
    window.hsConversationsSettings = {
      identificationEmail: user.username,
      identificationToken: token,
      enableWidgetCookieBanner: true,
      disableAttachment: false,
    };
    if (refresh) {
      window.HubSpotConversations.widget.refresh();
    } else {
      window.HubSpotConversations.widget.load();
    }
  }
}
